import {LitElement} from 'lit';
import Translations from './translations.js';

/**
 * Clase base para los webcomponents del proyecto.
 *
 * @see jfWC
 */
export default class Base extends LitElement {
    /**
     * Devuelve el nombre del componente sin prefijo.
     * Por defecto se usa el nombre de la clase convertido a `kebab-case`.
     *
     * @return {string} Nombre del componente.
     */
    static get cname() {
        return this.decamelize(this.name);
    }

    /**
     * Devuelve el nombre del componente con el cual se registrará.
     *
     * @return {string} Nombre del componente.
     */
    static get is() {
        return `${this.prefix}-${this.cname}`;
    }

    /**
     * Devuelve el prefijo de los componentes.
     * Este método facilita la creación de paquetes ya que cada paquete
     * sobrescribe esta propiedad.
     *
     * @return {string} Prefijo a usar.
     */
    static get prefix() {
        return 'inv';
    }

    /**
     * Convierte un texto en `CamelCase` a un texto separado por un carácter.
     *
     * @param {string} text Texto a convertir.
     * @param {string} sep  Separador a usar (`-` por defecto).
     *
     * @return {string}
     */
    static decamelize(text, sep = '-') {
        return text.replace(/([^A-ZÑÁÉÍÓÚ])([A-ZÑÁÉÍÓÚ])/gu, '$1' + sep + '$2').toLowerCase();
    }

    /**
     * Convierte un texto separado por un carácter en `PascalCase`.
     *
     * @param {string} text Texto a convertir.
     *
     * @return {string}
     */
    static pascalize(text = '-') {
        return text.replace(/(^[a-zñáéíóú])|(([^a-zñáéíóú0-9])([a-zñáéíóú0-9]))/gu, (matchedString) =>
            matchedString.replace(/([^a-zñáéíóú0-9])/gu, '').toUpperCase()
        );
    }

    /**
     * Convierte el texto pasado a mayúsculas
     * @param text
     * @returns {string}
     */
    static capitalize(text) {
        {
            return text ? text[0].toUpperCase() + text.substring(1) : '';
        }
    }

    /**
     * Registra el componente.
     */
    static register() {
        const is = this.is;
        if (!customElements.get(is)) {
            if (window.inveert?.debug) {
                console.log('Registrando componente: %s', is);
            }
            customElements.define(is, this);
        }
    }

    /**
     * Traduce el texto pasado.
     * @param {string} label - Texto a traducir
     * @param {Array|object} args - Argumentos a sustituir en el texto
     * @returns {string}
     */
    static tr(label, ...args) {
        return Translations.i().tr(label, ...args);
    }

    /**
     * Traduce el texto pasado.
     * @param {string} label - Texto a traducir
     * @param {Array|object} args - Argumentos a sustituir en el texto
     * @returns {string}
     */
    tr(label, ...args) {
        return this.constructor.tr(label, ...args);
    }

    /**
     * Emite el evento con los datos especificados.
     *
     * @param {string}  event   Nombre del evento a emitir.
     * @param {*?}      data    Datos del evento.
     * @param {object?} options Opciones del evento.
     */
    emit(event, data = null, options = {}) {
        if (event) {
            options = {
                bubbles: true,
                composed: true,
                ...options,
            };

            if (data !== undefined && data !== null) {
                options.detail = data;
            }
            this.dispatchEvent(new CustomEvent(`${this.constructor.cname}-${event}`, options));
        }
    }

    /**
     * Obtiene un id aleatorio
     *
     * @return {string}
     */
    getId() {
        return this.id || (this.id = Math.random().toFixed(10).substring(2));
    }

    /**
     * Crea el manejador del evento con los datos especificados.
     *
     * @param {string}  event   Nombre del evento a emitir.
     * @param {*?}      data    Datos del evento.
     * @param {object?} options Opciones del evento.
     */
    buildHandler(event, data = null, options = {}) {
        const handler = 'on' + Base.pascalize(event);

        return typeof this[handler] === 'function'
            ? data
                ? this[handler].bind(this, {data})
                : this[handler].bind(this)
            : (e) => this.emit(event, {value: e.target.value, ...(data ?? {})}, options);
    }

    /**
     * Obtiene un string con todos las propiedades pasados si estan presentes
     * en el componente
     *
     * @param {string|Array}  prop   nombres de las propiedades que pueden estar presentes o no.
     *
     * @return {string}
     */
    getCss(prop) {
        return Array.isArray(prop) ? prop.map(this.getCss, this).join(' ') : this[prop] ? prop : '';
    }

    /**
     * Devuelve el primer elemento que coincide con el id pasado
     *
     * @param {string}  id   id del elemento que queremos recuperar
     *
     * @return {Element}
     */
    _getElementById(id) {
        return this.renderRoot?.querySelector(`#${id}`);
    }

    /**
     * Añade las clases que se pasen al elemento indicado por el id
     *
     * @param {string}  id   id del elemento al que queremos añadir las clases
     */
    addClasses(id, ...classes) {
        this._getElementById(id)?.classList.add(...classes);
    }

    /**
     * Elimina las clases que se pasen al elemento indicado por el id
     *
     * @param {string}  id   id del elemento al que queremos eliminar las clases
     */
    removeClasses(id, ...classes) {
        this._getElementById(id)?.classList.remove(...classes);
    }

    /**
     * Añade una lista de elementos a un nuevo DocumentFragment
     *
     * @param {Array}  children   lista de elementos a añadir
     * @param {Element}  parent   elemento padre donde añadir los children
     */
    appendChildren(children, parent) {
        if (children?.length) {
            const fragment = document.createDocumentFragment();
            children.forEach(fragment.appendChild, fragment);
            parent.appendChild(fragment);
        }
    }

    /**
     * Crea varios elementos del mismo tipo con el contenido especificado y los agrega al
     * nodo padre. El contenido puede ser un array de objetos que sería añadidos como atributos,
     * elementos, números o cadenas
     *
     * @param {string}  tagname   nombre del elemento a crear
     * @param {Array<Object | Element | number | string>}  attributes   lista de contenido a añadir
     * @param {Element}  parent   elemento padre donde añadir el elemento
     *
     * @return {Element} elemento padre
     */
    createElements(tagname, attributes, parent) {
        if (attributes?.length) {
            this.appendChildren(
                attributes.map((value) => {
                    const node = document.createElement(tagname);
                    if (value instanceof Element) {
                        node.appendChild(value);
                    } else if (typeof value === 'object') {
                        this.merge(node, value);
                    } else {
                        node.innerHTML = value;
                    }

                    return node;
                }),
                parent
            );
        }
        return parent;
    }

    /**
     * Comprueba si el item pasado es un objeto
     * @param item
     * @returns {boolean}
     */
    isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }

    /**
     * Añade recursivamente las propiedades del objeto src al objeto dst
     * @param dst
     * @param src
     * @returns {*}
     */
    merge(dst, src) {
        if (this.isObject(dst) && this.isObject(src)) {
            for (const key in src) {
                const current = dst[key];
                const value = src[key];
                if (this.isObject(current) && this.isObject(value)) {
                    this.merge(current, value);
                } else {
                    dst[key] = value;
                }
            }
        }

        return dst;
    }

    /**
     * Obtiene las hojas de estilos a usar.
     * @return {CSSStyleSheet[]} Hojas de estilos.
     */
    getStyleSheets() {
        return [];
    }

    /**
     * Carga las hojas de estilos cuando el componente se conecta al DOM y si hay hojas de estilo que cargar.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this.isConnected) {
            const styleSheets = this.getStyleSheets();
            if (styleSheets.length) {
                this.shadowRoot.adoptedStyleSheets = styleSheets;
            }
        }
    }
}
